import React from 'react';
import { Grid, Typography, TypographyProps } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AccountData, DiscountOut, DiscountType } from '../api';
import { getCurrencySymbol } from '../utils/utils';
import { useAuth } from '../session/InternalAuthProvider';
import { usePriceFormatter } from './usePriceFormatter';

export const formatDiscountValue = (
  value: number | undefined,
  type: DiscountType | undefined,
  currencySymbol: string,
): string => {
  if (value == null) return '';
  return type === DiscountType.PERCENT ? `${value}%` : `${value}${currencySymbol}`;
};

export const calculateDiscountAmount = (
  price: number,
  discount: { type?: DiscountType; value?: number } | undefined,
): number => {
  if (!discount || discount.value == null) return 0;
  if (discount.type === DiscountType.PERCENT) {
    return -(price * discount.value) / 100;
  }
  if (discount.type === DiscountType.FIXED) {
    return -discount.value;
  }
  return 0;
};

type Props = {
  discount?: { code?: string; value: number; type: DiscountType } | Pick<DiscountOut, 'code' | 'value' | 'type'>;
  initialPrice?: number;
  variant?: TypographyProps['variant'];
  showLabel?: boolean;
  showCalculation?: boolean;
};

export const DiscountDetails = ({
  discount,
  initialPrice,
  variant = 'body2',
  showLabel = true,
  showCalculation = true,
}: Props) => {
  const { t } = useTranslation();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const priceFormatter = usePriceFormatter();
  const currencySymbol = getCurrencySymbol(account);

  if (!discount) return null;

  const valueLabel = formatDiscountValue(discount.value, discount.type, currencySymbol);
  const calc = showCalculation && initialPrice != null ? calculateDiscountAmount(initialPrice, discount) : undefined;

  return (
    <Grid container gap={0.5} alignItems="center" flexWrap="nowrap" sx={{ width: 'auto' }}>
      {showLabel && (
        <Typography variant={variant} color="text.secondary">
          {`${t('calendar.order_popper.discountCode')}:`}
        </Typography>
      )}
      {discount.code && <Typography variant={variant}>{discount.code}</Typography>}
      {valueLabel && <Typography variant={variant}>{`(${valueLabel})`}</Typography>}
      {calc != null && calc !== 0 && (
        <Typography variant={variant} color="error.main">
          {priceFormatter(calc)}
        </Typography>
      )}
    </Grid>
  );
};
